import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { buildClasses } from "../utils/buildClasses";
import { BudgetTrackingMock } from "./BudgetTrackingMock";
import { HEADER_HEIGHT_PX } from "./Header";
import { MoneyFlowMock } from "./MoneyFlowMock";
import { TransactionsMock } from "./TransactionsMock";

// The landing page's feature tour, modeled on chatgpt.com/overview. The moment
// the hero's Get Started button has scrolled out of view, the app mock glides
// into a right-hand column in one timed motion, shrinking to fit, and pins
// there. An accordion of the steps appears beside it. For the next stretch of
// scrolling the page stays put: each step in turn opens, its screen shows in the
// mock, and a line across its top fills as the scroll moves through it.
// After the last step the whole tour scrolls away. Scrolling back up past the
// docking point glides the mock home again.
//
// The glide is timed rather than tied to scroll position, so it can't be left
// half-finished mid-scroll -- a half-slid mock just looked broken. The filling
// line is tied to scroll position, so the pinned stretch never feels stuck.
//
// Steps are plain data, and the mock only ever reads the active step's index.
//
// Below lg there's no room beside the mock, so the steps stack down the page
// as cards instead (see StackedTour).
//
// Prerendering note: at rest the mock is the table at full width, every step's
// copy is in the markup, and the other screens are in the DOM at zero opacity.

type Screen = "transactions" | "moneyFlow" | "budgets";

type Step = {
  heading: string;
  // One entry per paragraph.
  body: string[];
  screen: Screen;
};

const STEPS: Step[] = [
  {
    heading: "Start with data you control",
    body: [
      "Charts and graphs are only useful if you understand how your transactions are categorized.",
      "Powerful-but-practical automation eliminates tedium while keeping you in control. Bread doesn’t make confusing assumptions, so you can categorize your transactions in ways that actually make sense to you.",
    ],
    screen: "transactions",
  },
  {
    heading: "See how your money flows",
    body: [
      "Understand your income, expenses, and savings with reports that turn your data into useful insights.",
    ],
    screen: "moneyFlow",
  },
  {
    heading: "Plan ahead with practical budgets",
    body: [
      "Track your activity against a budget so you can spend with confidence and pursue your goals.",
    ],
    screen: "budgets",
  },
];

const TOUR_QUERY = "(min-width: 1024px)";

// The copy column's width, as a fraction of the page's content width, and the
// gap between it and the docked mock.
const COPY_COLUMN_FRACTION = 0.34;
const COPY_TO_MOCK_GAP_PX = 48;

// The docked mock shrinks only as far as it must to fit beside the copy and
// within the window's height. Below MIN_SCALE its text stops being legible, so
// past that point it holds its size and runs off the right edge instead.
const MIN_SCALE = 0.55;
const MAX_SCALE = 0.9;
const VERTICAL_MARGIN_PX = 48;

// Scrolling this far back above the docking point undocks the mock. Without the
// allowance, resting exactly on the boundary could flicker between the two.
const UNDOCK_HYSTERESIS_PX = 24;

// How far below the docked mock's top edge the accordion starts.
const ACCORDION_TOP_INSET_PX = 32;

// How the pinned stretch's scrolling is spent, in pixels: a lead-in while the
// mock finishes docking and the accordion appears, then each step in turn, then
// a short hold on the last step before the tour lets go.
const ENTRY_SCROLL_PX = 240;
const STEP_SCROLL_PX = 500;
const EXIT_SCROLL_PX = 160;
const PINNED_SCROLL_PX =
  ENTRY_SCROLL_PX + STEPS.length * STEP_SCROLL_PX + EXIT_SCROLL_PX;

function ScreenMock(props: { screen: Screen }): JSX.Element {
  switch (props.screen) {
    case "transactions":
      return <TransactionsMock />;
    case "moneyFlow":
      return <MoneyFlowMock />;
    case "budgets":
      return <BudgetTrackingMock />;
  }
}

// Every screen is mounted at once and crossfaded, so switching steps never
// waits on a chart laying itself out.
function MockStack(props: { activeStep: number }): JSX.Element {
  return (
    <div className={buildClasses("relative")}>
      {STEPS.map((step, index) => (
        <div
          key={step.screen}
          aria-hidden={index !== props.activeStep}
          className={buildClasses(
            "transition-opacity",
            "duration-500",
            "motion-reduce:transition-none",
            {
              // The first screen stays in flow to give the stack its size.
              if: index === 0,
              then: buildClasses("relative"),
              else: buildClasses("absolute", "inset-0"),
            },
            {
              if: index === props.activeStep,
              then: buildClasses("opacity-100"),
              else: buildClasses("pointer-events-none", "opacity-0"),
            },
          )}
        >
          <ScreenMock screen={step.screen} />
        </div>
      ))}
    </div>
  );
}

// Below lg, StackedTour draws the mocks at this width in their desktop layout
// and shrinks them to fit the page. Their phone layouts need more work before
// they're worth showing; a shrunken screenshot of the real app reads better.
const STACKED_MOCK_DESIGN_WIDTH_PX = 1280;
// Space between a step's mock and its copy, and between one step and the next.
const STACKED_COPY_GAP_PX = 24;
const STACKED_STEP_GAP_PX = 96;
// At the top of the page, the first step's copy starts at least this far below
// the bottom of the window.
const STACKED_OFFSCREEN_PX = 24;
// Scrolling past this lifts the first step's copy up under its mock; scrolling
// back above STACKED_LOWER_SCROLL_PX drops it again. The gap between the two
// keeps resting near the boundary from flickering between them.
const STACKED_LIFT_SCROLL_PX = 16;
const STACKED_LOWER_SCROLL_PX = 4;

// The tour below lg: each step is a card, its screen with its copy under it,
// stacked down the page. Nothing is pinned.
//
// So the page opens on the hero and the first screen alone, the first step's
// copy starts below the fold. The moment the page scrolls, it glides up to sit
// under its screen, in the same timed motion as the desktop tour's docking
// glide, and glides back down on returning to the top.
function StackedTour(): JSX.Element {
  const tourRef = useRef<HTMLDivElement>(null);
  const mockRef = useRef<HTMLDivElement>(null);
  const liftedRef = useRef(false);
  // The window's height when the width last changed. Mobile browsers change
  // the height as their toolbars show and hide mid-scroll, and re-measuring
  // the drop then would make the copy jump.
  const windowHeightRef = useRef(window.innerHeight);

  const [mockLayout, setMockLayout] = useState<{
    scale: number;
    height: number;
  } | null>(null);
  // How far below its resting place the first step's copy starts.
  const [drop, setDrop] = useState(0);
  // Read synchronously, so a page reloaded partway down starts lifted rather
  // than animating on load.
  const [lifted, setLifted] = useState(
    () => window.scrollY > STACKED_LIFT_SCROLL_PX,
  );
  liftedRef.current = lifted;

  // The mocks' scale, which follows the page's width. Every screen shares the
  // frame's proportions, so measuring one sizes them all.
  useLayoutEffect(() => {
    const tour = tourRef.current;
    const mock = mockRef.current;
    if (tour === null || mock === null) return;

    let lastWidth = -1;
    const measure = () => {
      const width = tour.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      windowHeightRef.current = window.innerHeight;
      // offsetHeight ignores the scale transform: this is the design height.
      const designHeight = mock.offsetHeight;
      if (width === 0 || designHeight === 0) return;
      const scale = width / STACKED_MOCK_DESIGN_WIDTH_PX;
      setMockLayout({ scale, height: designHeight * scale });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(tour);
    return () => observer.disconnect();
  }, []);

  // The drop depends on where the tour sits on the page, and the hero above
  // can still change height after this first runs (its subtitle is sized once
  // fonts load), so it's re-measured whenever the page's size changes. The
  // lifted copy doesn't count: the drop is measured from its resting place.
  useLayoutEffect(() => {
    const tour = tourRef.current;
    if (tour === null || mockLayout === null) return;

    const measure = () => {
      const tourTop = tour.getBoundingClientRect().top + window.scrollY;
      const restingCopyTop = tourTop + mockLayout.height + STACKED_COPY_GAP_PX;
      setDrop(
        Math.max(
          0,
          windowHeightRef.current + STACKED_OFFSCREEN_PX - restingCopyTop,
        ),
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    return () => observer.disconnect();
  }, [mockLayout]);

  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      if (!liftedRef.current && scrollY > STACKED_LIFT_SCROLL_PX) {
        setLifted(true);
      } else if (liftedRef.current && scrollY < STACKED_LOWER_SCROLL_PX) {
        setLifted(false);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div ref={tourRef}>
      {STEPS.map((step, index) => (
        <div
          key={step.screen}
          style={{ marginTop: index === 0 ? 0 : STACKED_STEP_GAP_PX }}
        >
          <div
            className={buildClasses("pointer-events-none")}
            style={
              mockLayout === null
                ? { visibility: "hidden" }
                : { height: mockLayout.height }
            }
          >
            <div
              ref={index === 0 ? mockRef : undefined}
              className={buildClasses("mock-desktop", "origin-top-left")}
              style={{
                width: STACKED_MOCK_DESIGN_WIDTH_PX,
                transform:
                  mockLayout === null
                    ? undefined
                    : `scale(${mockLayout.scale})`,
              }}
            >
              <ScreenMock screen={step.screen} />
            </div>
          </div>

          {/* The first step's copy drops by a margin rather than a transform,
              so the steps below it move with it instead of being overlapped. */}
          <div
            className={buildClasses({
              if: index === 0,
              then: buildClasses(
                "transition-[margin-top]",
                "duration-700",
                "ease-in-out",
                "motion-reduce:transition-none",
              ),
            })}
            style={{
              marginTop:
                STACKED_COPY_GAP_PX + (index === 0 && !lifted ? drop : 0),
            }}
          >
            <h2
              className={buildClasses(
                "text-2xl",
                "font-semibold",
                "tracking-tight",
                "text-on-surface-800",
              )}
            >
              {step.heading}
            </h2>
            <div
              className={buildClasses(
                "mt-3",
                "flex",
                "flex-col",
                "gap-3",
                "text-lg",
                "leading-relaxed",
                "text-on-surface-600",
              )}
            >
              {step.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

type DockedLayout = {
  // Where the mock sticks, which is also where it docks. Set so that it sticks
  // at exactly the moment the dock-after element has scrolled out of view.
  stickyTop: number;
  // The docked transform: a shift right, a shift down to center the shrunken
  // mock vertically, and the scale.
  left: number;
  offsetY: number;
  scale: number;
};

function StickyTour(props: {
  dockAfterRef: React.RefObject<HTMLElement>;
}): JSX.Element {
  const tourRef = useRef<HTMLDivElement>(null);
  const mockRef = useRef<HTMLDivElement>(null);
  const accordionRef = useRef<HTMLDivElement>(null);
  const dockedRef = useRef(false);

  const [activeStep, setActiveStep] = useState(0);
  const [docked, setDocked] = useState(false);
  const [layout, setLayout] = useState<DockedLayout | null>(null);

  // Size-dependent values, measured before paint so the resting layout never
  // jumps.
  useLayoutEffect(() => {
    const tour = tourRef.current;
    const mock = mockRef.current;
    const dockAfter = props.dockAfterRef.current;
    if (tour === null || mock === null || dockAfter === null) return;

    const measure = () => {
      const height = window.innerHeight;
      const contentWidth = tour.offsetWidth;
      // offsetHeight ignores transforms, so this is the unscaled height.
      const mockHeight = mock.offsetHeight;

      const left = contentWidth * COPY_COLUMN_FRACTION + COPY_TO_MOCK_GAP_PX;
      // Room to the right of the copy, stopping at the same margin the page
      // keeps on the left. The mock's unscaled width is the content width.
      const widthFit = (contentWidth - left) / contentWidth;
      // The sticky header covers the top of the window, so the mock only has
      // the space below it.
      const available = height - HEADER_HEIGHT_PX;
      const heightFit = (available - 2 * VERTICAL_MARGIN_PX) / mockHeight;
      const scale = Math.max(
        MIN_SCALE,
        Math.min(MAX_SCALE, widthFit, heightFit),
      );

      // Both rects move together as the page scrolls, so their difference is
      // the same at any scroll position. The mock docks when the button has
      // gone under the sticky header, which is when it stops being visible.
      const stickyTop =
        HEADER_HEIGHT_PX +
        Math.max(
          0,
          tour.getBoundingClientRect().top -
            dockAfter.getBoundingClientRect().bottom,
        );
      const bandTop =
        HEADER_HEIGHT_PX + Math.max(0, (available - mockHeight * scale) / 2);

      setLayout({ stickyTop, left, offsetY: bandTop - stickyTop, scale });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(tour);
    observer.observe(mock);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [props.dockAfterRef]);

  // On each scroll frame: whether the mock is docked, which step is open, and
  // how far through its step each line has filled.
  useEffect(() => {
    const tour = tourRef.current;
    if (tour === null || layout === null) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      // How far the page has scrolled since the mock docked.
      const scrolled = layout.stickyTop - tour.getBoundingClientRect().top;

      const isDocked = dockedRef.current
        ? scrolled >= -UNDOCK_HYSTERESIS_PX
        : scrolled >= 0;
      dockedRef.current = isDocked;
      setDocked(isDocked);

      const intoSteps = scrolled - ENTRY_SCROLL_PX;
      STEPS.forEach((_, index) => {
        const progress = Math.min(
          1,
          Math.max(0, (intoSteps - index * STEP_SCROLL_PX) / STEP_SCROLL_PX),
        );
        // Set directly rather than through state, so filling the lines doesn't
        // re-render the tour on every scroll frame.
        accordionRef.current?.style.setProperty(
          `--tour-progress-${index}`,
          String(progress),
        );
      });
      setActiveStep(
        Math.min(
          STEPS.length - 1,
          Math.max(0, Math.floor(intoSteps / STEP_SCROLL_PX)),
        ),
      );
    };
    const onScroll = () => {
      if (frame === 0) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [layout]);

  // Clicking a step scrolls to the start of its stretch, which opens it.
  const openStep = (index: number) => {
    const tour = tourRef.current;
    if (tour === null || layout === null) return;
    const dockedScrollY =
      tour.getBoundingClientRect().top + window.scrollY - layout.stickyTop;
    window.scrollTo({
      top: dockedScrollY + ENTRY_SCROLL_PX + index * STEP_SCROLL_PX + 1,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  };

  return (
    // The sticky stage stays pinned until the end of the tour reaches its
    // bottom, so the spacer after it is exactly the pinned stretch.
    <div ref={tourRef}>
      <div
        className={buildClasses("sticky", "z-20")}
        style={{ top: layout?.stickyTop ?? 0 }}
      >
        <div
          ref={mockRef}
          className={buildClasses(
            "pointer-events-none",
            "origin-top-left",
            "transition-transform",
            "duration-700",
            "ease-in-out",
            "motion-reduce:transition-none",
          )}
          style={{
            transform:
              docked && layout !== null
                ? `translate(${layout.left}px, ${layout.offsetY}px) scale(${layout.scale})`
                : "none",
          }}
        >
          <MockStack activeStep={activeStep} />
        </div>

        {/* Just below the docked mock's top edge. It appears once the glide
            is underway, and until then it's hidden and can't be clicked. */}
        <div
          ref={accordionRef}
          className={buildClasses(
            "absolute",
            "left-0",
            "transition-[opacity,transform]",
            "duration-500",
            "motion-reduce:transition-none",
            {
              if: docked,
              then: buildClasses("translate-x-0", "opacity-100", "delay-200"),
              else: buildClasses(
                "pointer-events-none",
                "-translate-x-6",
                "opacity-0",
              ),
            },
          )}
          style={{
            top: (layout?.offsetY ?? 0) + ACCORDION_TOP_INSET_PX,
            width: `${COPY_COLUMN_FRACTION * 100}%`,
          }}
        >
          <StepAccordion activeStep={activeStep} onOpenStep={openStep} />
        </div>
      </div>

      <div aria-hidden style={{ height: PINNED_SCROLL_PX }} />
    </div>
  );
}

// The steps as an accordion: only the active step is open, the rest are just
// their headings, shrunk. A line along each step's top edge fills as the
// scroll moves through that step, driven by the --tour-progress-<index>
// properties StickyTour sets on an ancestor.
function StepAccordion(props: {
  activeStep: number;
  onOpenStep: (index: number) => void;
}): JSX.Element {
  return (
    <div>
      {STEPS.map((step, index) => {
        const active = index === props.activeStep;
        return (
          <div
            key={step.screen}
            className={buildClasses(
              "relative",
              "border-t",
              "border-on-surface-200",
            )}
          >
            <div
              aria-hidden
              className={buildClasses(
                "absolute",
                "inset-x-0",
                "origin-left",
                "transition-[top,height,background-color]",
                "duration-300",
                "motion-reduce:transition-none",
                // While the open step's line fills it's purple and a little
                // thicker, centered on the divider, so it's clearer it's
                // moving. Steps already passed keep a thin full line in gray.
                active
                  ? buildClasses("top-[-2px]", "h-[3px]", "bg-purple")
                  : buildClasses("-top-px", "h-px", "bg-on-surface-500"),
              )}
              style={{ transform: `scaleX(var(--tour-progress-${index}, 0))` }}
            />
            <h2>
              <button
                type="button"
                aria-expanded={active}
                onClick={() => props.onOpenStep(index)}
                className={buildClasses(
                  "w-full",
                  "py-5",
                  "text-left",
                  "font-semibold",
                  "tracking-tight",
                  "transition-[font-size,color]",
                  "duration-300",
                  "motion-reduce:transition-none",
                  {
                    if: active,
                    then: buildClasses("text-3xl", "text-on-surface-800"),
                    else: buildClasses(
                      "text-xl",
                      "text-on-surface-400",
                      "hover:text-on-surface-600",
                    ),
                  },
                )}
              >
                {step.heading}
              </button>
            </h2>
            {/* Animating the row from 0fr to 1fr opens it to its content's own
                height, which a height transition can't do. */}
            <div
              className={buildClasses(
                "grid",
                "transition-[grid-template-rows,opacity]",
                "duration-500",
                "motion-reduce:transition-none",
                {
                  if: active,
                  then: buildClasses("grid-rows-[1fr]", "opacity-100"),
                  else: buildClasses("grid-rows-[0fr]", "opacity-0"),
                },
              )}
            >
              <div className={buildClasses("min-h-0", "overflow-hidden")}>
                <div
                  className={buildClasses(
                    "flex",
                    "flex-col",
                    "gap-4",
                    "pb-6",
                    "text-lg",
                    "leading-relaxed",
                    "text-on-surface-600",
                  )}
                >
                  {step.body.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function subscribeToTourQuery(onChange: () => void): () => void {
  const query = window.matchMedia(TOUR_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

export function FeatureTour(props: {
  // The mock docks the moment this element has scrolled out of view.
  dockAfterRef: React.RefObject<HTMLElement>;
}): JSX.Element {
  const sticky = useSyncExternalStore(
    subscribeToTourQuery,
    () => window.matchMedia(TOUR_QUERY).matches,
    // The prerendered HTML always holds the sticky tour, and hydration must
    // start from exactly that markup. On a narrow window React hydrates the
    // sticky tour and then swaps in the stacked one, instead of treating the
    // difference as a failed hydration and redrawing the whole page.
    () => true,
  );

  return sticky ? (
    <StickyTour dockAfterRef={props.dockAfterRef} />
  ) : (
    <StackedTour />
  );
}
